import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { getInstanceMonitor, type InstanceMonitorDto } from '@/services/aiStudioMonitorService';

interface InstanceMonitorPanelProps {
  instanceId: string;
}

export const InstanceMonitorPanel: React.FC<InstanceMonitorPanelProps> = ({ instanceId }) => {
  const { t } = useLanguage();
  const [monitor, setMonitor] = useState<InstanceMonitorDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getInstanceMonitor(instanceId)
      .then((data) => {
        if (!cancelled) setMonitor(data);
      })
      .catch(() => {
        if (!cancelled) setMonitor(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  if (loading) {
    return <p className="text-base text-[var(--color-surface-text-secondary)]">{t('aiStudio.monitor.loading')}</p>;
  }

  if (!monitor) {
    return <p className="text-base text-[var(--color-error)]">{t('aiStudio.monitor.loadFailed')}</p>;
  }

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4">
      <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.monitor.title')}
      </h2>

      <dl className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-base text-[var(--color-surface-text-secondary)]">{t('aiStudio.monitor.setupStatus')}</dt>
          <dd className="text-base text-[var(--color-surface-text)]">
            {monitor.setupStatus?.status ?? '—'}
          </dd>
        </div>
        <div>
          <dt className="text-base text-[var(--color-surface-text-secondary)]">{t('aiStudio.monitor.versionPin')}</dt>
          <dd className="text-base text-[var(--color-surface-text)]">v{monitor.versionNumber}</dd>
        </div>
        <div>
          <dt className="text-base text-[var(--color-surface-text-secondary)]">{t('aiStudio.monitor.lastRun')}</dt>
          <dd className="text-base text-[var(--color-surface-text)]">
            {monitor.lastRunAt ? new Date(monitor.lastRunAt).toLocaleString() : t('aiStudio.common.none')}
          </dd>
        </div>
        <div>
          <dt className="text-base text-[var(--color-surface-text-secondary)]">{t('aiStudio.monitor.failedRuns')}</dt>
          <dd className="text-base text-[var(--color-surface-text)]">{monitor.failedRunCount}</dd>
        </div>
      </dl>

      {monitor.upgradeAvailable && (
        <p className="mt-2 text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.monitor.upgradeAvailable', { count: monitor.upgradeCount })}
        </p>
      )}

      {monitor.recentRuns.length > 0 && (
        <div className="mt-3">
          <h3 className="text-base font-medium text-[var(--color-surface-text)]">
            {t('aiStudio.monitor.recentRuns')}
          </h3>
          <ul className="mt-1 flex flex-col gap-1">
            {monitor.recentRuns.map((run) => (
              <li key={run.id}>
                <Link to={`/ai-studio/runs/${run.id}`} className="text-base text-[var(--color-info)] underline">
                  {run.run_name} — {run.status}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
